
'use client';

import { useEffect, useCallback } from 'react';
import { useToast } from './use-toast';
import { Button } from '@/components/ui/button';
import { getMessaging, getToken } from 'firebase/messaging';
import { useFirebase } from '@/firebase';

const REMINDER_KEY_PREFIX = 'birthday-reminder-';

// Function to request FCM token
export const requestForToken = async (
  firebaseApp: any,
  vapidKey: string,
  onTokenReceived: (token: string) => void
) => {
  try {
    const messaging = getMessaging(firebaseApp);
    const currentToken = await getToken(messaging, {
      vapidKey: vapidKey,
    });

    if (currentToken) {
      console.log('User FCM Token:', currentToken);
      onTokenReceived(currentToken); // Callback to save the token
    } else {
      console.log(
        'No registration token available. Request permission to generate one.'
      );
    }
  } catch (err) {
    console.log('An error occurred while retrieving token. ', err);
  }
};


export function useNotifications() {
  const { toast } = useToast();
  const { firebaseApp, user } = useFirebase();
  
  // You need to generate this key in your Firebase project settings
  // Project settings > Cloud Messaging > Web configuration > Generate key pair
  const VAPID_KEY = 'BJMooG-g2BEUwiM0QAGZ55VY49xF9UD4a2j3gJFjqlSJ1vFqLZ6chpT5JGRXXn4B2nMbBsy-6BwCv5mcarmUQbM';

  const requestPermission = useCallback(() => {
    if (!('Notification' in window)) {
      console.log('This browser does not support desktop notification');
      return;
    }

    if (Notification.permission === 'default') {
      Notification.requestPermission().then((permission) => {
        if (permission === 'granted') {
          toast({
            title: 'Notifications Enabled',
            description: 'You can now set birthday reminders.',
          });
          if (firebaseApp && user) {
            requestForToken(firebaseApp, VAPID_KEY, (token) => {
                // Here you would typically save the token to your database
                // For now, we just log it. The save logic is in useBirthdays
            });
          }
        } else {
          toast({
            variant: 'destructive',
            title: 'Notifications Denied',
            description: 'You will not be able to set reminders.',
          });
        }
      });
    } else if (Notification.permission === 'granted' && firebaseApp && user) {
         requestForToken(firebaseApp, VAPID_KEY, (token) => {
            // Here you would typically save the token to your database
         });
    }
  }, [toast, firebaseApp, user]);

  useEffect(() => {
    // Request permission on initial load
    requestPermission();

    // Set up an interval to check permission status
    const intervalId = setInterval(() => {
      if ('Notification' in window && Notification.permission !== 'granted') {
        toast({
          title: 'Enable Notifications for Timely Alerts',
          description: 'Timely alerts for birthdays and anniversaries require notification permissions. Please consider enabling them in your browser settings.',
          action: (
            <Button onClick={requestPermission} size="sm">
              Enable
            </Button>
          ),
        });
      }
    }, 30000); // Check every 30 seconds

    // Clean up the interval on component unmount
    return () => clearInterval(intervalId);
  }, [requestPermission, toast]);

  const scheduleNotification = useCallback(
    (date: Date, title: string, body: string) => {
      if (Notification.permission !== 'granted') {
        toast({
          variant: 'destructive',
          title: 'Permission Required',
          description: 'Please enable notifications to set a reminder.',
        });
        requestPermission();
        return;
      }

      // Set reminder for 9 AM on the day of the birthday
      const reminderTime = new Date(date);
      reminderTime.setHours(9, 0, 0, 0);

      const now = new Date();

      if (reminderTime < now) {
         toast({
          variant: 'destructive',
          title: 'Cannot Set Reminder',
          description: 'The birthday has already passed for this year.',
        });
        return;
      }
      
      const uniqueId = `${REMINDER_KEY_PREFIX}${date.getTime()}-${title}`;

      // This is a simplified scheduling mechanism using localStorage.
      // A more robust solution would use a service worker.
      try {
        localStorage.setItem(uniqueId, JSON.stringify({ reminderTime: reminderTime.toISOString(), title, body }));
        toast({
          title: 'Reminder Set!',
          description: "You'll be notified locally on your device.",
        });
      } catch (error) {
        console.error('Could not save reminder to localStorage', error);
        toast({
          variant: 'destructive',
          title: 'Error Setting Reminder',
          description: 'Could not save the reminder. Your browser storage might be full.',
        });
      }
    },
    [toast, requestPermission]
  );
  
  // This effect checks for scheduled notifications on app load
  useEffect(() => {
    if (!('Notification' in window) || Notification.permission !== 'granted') {
      return;
    }
    
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith(REMINDER_KEY_PREFIX)) {
        try {
          const item = JSON.parse(localStorage.getItem(key)!);
          const reminderTime = new Date(item.reminderTime);
          const now = new Date();

          if (reminderTime <= now) {
            new Notification(item.title, {
              body: item.body,
              icon: '/favicon.ico', // Optional: add an icon
            });
            localStorage.removeItem(key); // Clean up after showing
          } else {
            // Re-schedule check for when the app might be open next
            const timeout = reminderTime.getTime() - now.getTime();
            setTimeout(() => {
              new Notification(item.title, { body: item.body });
              localStorage.removeItem(key);
            }, timeout);
          }
        } catch (error) {
          console.error('Error processing reminder from localStorage', error);
          localStorage.removeItem(key);
        }
      }
    });

  }, []);

  return { requestPermission, scheduleNotification };
}
